import React, { useRef } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { Document, Page, View, Text, Image, StyleSheet, pdf } from '@react-pdf/renderer';
import { formatMoney } from '../../Tools/helpers';

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Title, Tooltip, Legend);

// ---------------------------------------------------------------------------
// PDF Document
// ---------------------------------------------------------------------------

const fmt = (value) =>
  new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(value);

const pdfStyles = StyleSheet.create({
  page: {
    fontFamily: 'Helvetica',
    fontSize: 8,
    paddingTop: 30,
    paddingBottom: 30,
    paddingHorizontal: 30,
    flexDirection: 'column',
  },
  title: {
    fontSize: 13,
    fontFamily: 'Helvetica-Bold',
    marginBottom: 14,
    color: '#1e3a5f',
  },
  // Table
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#e2e8f0',
    borderBottomWidth: 1,
    borderBottomColor: '#cbd5e1',
    paddingVertical: 5,
    paddingHorizontal: 4,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
    paddingVertical: 4,
    paddingHorizontal: 4,
  },
  tableRowAlt: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
    paddingVertical: 4,
    paddingHorizontal: 4,
    backgroundColor: '#f8fafc',
  },
  colName: { flex: 3, fontFamily: 'Helvetica-Bold', fontSize: 7.5 },
  colNameVal: { flex: 3, fontSize: 7.5 },
  colNum: { flex: 1, textAlign: 'right', fontFamily: 'Helvetica-Bold', fontSize: 7.5 },
  colNumVal: { flex: 1, textAlign: 'right', fontSize: 7.5 },
  colBlue: { flex: 2, textAlign: 'right', fontFamily: 'Helvetica-Bold', fontSize: 7.5, color: '#1d4ed8' },
  colBlueVal: { flex: 2, textAlign: 'right', fontSize: 7.5, color: '#1d4ed8' },
  colGreen: { flex: 2, textAlign: 'right', fontFamily: 'Helvetica-Bold', fontSize: 7.5, color: '#15803d' },
  colGreenVal: { flex: 2, textAlign: 'right', fontSize: 7.5, color: '#15803d' },
  colRed: { flex: 2, textAlign: 'right', fontFamily: 'Helvetica-Bold', fontSize: 7.5, color: '#b91c1c' },
  colRedVal: { flex: 2, textAlign: 'right', fontSize: 7.5, color: '#b91c1c' },
  // Charts section
  chartsRow: {
    flexDirection: 'row',
    marginTop: 18,
    alignItems: 'flex-start',
  },
  barChartWrapper: {
    flex: 3,
    marginRight: 12,
  },
  doughnutWrapper: {
    flex: 2,
  },
  chartImage: {
    width: '100%',
  },
  chartLabel: {
    fontSize: 7,
    color: '#475569',
    textAlign: 'center',
    marginBottom: 4,
    fontFamily: 'Helvetica-Bold',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  // Summary cards row
  summaryRow: {
    flexDirection: 'row',
    marginTop: 14,
    gap: 8,
  },
  cardBlue: {
    flex: 1,
    borderWidth: 1,
    borderColor: '#bfdbfe',
    backgroundColor: '#eff6ff',
    borderRadius: 4,
    padding: 8,
  },
  cardGreen: {
    flex: 1,
    borderWidth: 1,
    borderColor: '#bbf7d0',
    backgroundColor: '#f0fdf4',
    borderRadius: 4,
    padding: 8,
  },
  cardRed: {
    flex: 1,
    borderWidth: 1,
    borderColor: '#fecaca',
    backgroundColor: '#fef2f2',
    borderRadius: 4,
    padding: 8,
  },
  cardLabel: {
    fontSize: 6.5,
    fontFamily: 'Helvetica-Bold',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 3,
  },
  cardLabelBlue: { color: '#2563eb' },
  cardLabelGreen: { color: '#16a34a' },
  cardLabelRed: { color: '#dc2626' },
  cardValue: {
    fontSize: 10,
    fontFamily: 'Helvetica-Bold',
  },
  cardValueBlue: { color: '#1e40af' },
  cardValueGreen: { color: '#166534' },
  cardValueRed: { color: '#991b1b' },
});

const FranchiseStatsPDF = ({ data, barImg, doughnutImg }) => {
  const grandTotalToPay = data.reduce((acc, row) => acc + (row.totalToPay || 0), 0);
  const grandTotalPaid  = data.reduce((acc, row) => acc + (row.totalPaid  || 0), 0);
  const grandTotalOwed  = data.reduce((acc, row) => acc + (row.totalOwed  || 0), 0);

  return (
    <Document>
      <Page size="A4" orientation="landscape" style={pdfStyles.page}>
        <Text style={pdfStyles.title}>Resumen por Franquicia</Text>

        {/* Table header */}
        <View style={pdfStyles.tableHeader}>
          <Text style={pdfStyles.colName}>FRANQUICIA</Text>
          <Text style={pdfStyles.colNum}>ALUMNOS</Text>
          <Text style={pdfStyles.colBlue}>TOTAL A PAGAR</Text>
          <Text style={pdfStyles.colGreen}>TOTAL PAGADO</Text>
          <Text style={pdfStyles.colRed}>TOTAL ADEUDADO</Text>
        </View>

        {/* Table rows */}
        {data.map((row, index) => (
          <View key={row.name} style={index % 2 === 0 ? pdfStyles.tableRow : pdfStyles.tableRowAlt}>
            <Text style={pdfStyles.colNameVal}>{row.name}</Text>
            <Text style={pdfStyles.colNumVal}>{row.students}</Text>
            <Text style={pdfStyles.colBlueVal}>{fmt(row.totalToPay || 0)}</Text>
            <Text style={pdfStyles.colGreenVal}>{fmt(row.totalPaid || 0)}</Text>
            <Text style={pdfStyles.colRedVal}>{fmt(row.totalOwed || 0)}</Text>
          </View>
        ))}

        {/* Charts side by side */}
        <View style={pdfStyles.chartsRow}>
          <View style={pdfStyles.barChartWrapper}>
            <Text style={pdfStyles.chartLabel}>Comparativo Financiero por Franquicia</Text>
            <Image src={barImg} style={pdfStyles.chartImage} />
          </View>
          <View style={pdfStyles.doughnutWrapper}>
            <Text style={pdfStyles.chartLabel}>Distribucion de Pagos vs Adeudos</Text>
            <Image src={doughnutImg} style={pdfStyles.chartImage} />
          </View>
        </View>

        {/* Summary cards */}
        <View style={pdfStyles.summaryRow}>
          <View style={pdfStyles.cardBlue}>
            <Text style={[pdfStyles.cardLabel, pdfStyles.cardLabelBlue]}>Total General a Pagar</Text>
            <Text style={[pdfStyles.cardValue, pdfStyles.cardValueBlue]}>{fmt(grandTotalToPay)}</Text>
          </View>
          <View style={pdfStyles.cardGreen}>
            <Text style={[pdfStyles.cardLabel, pdfStyles.cardLabelGreen]}>Total Pagado</Text>
            <Text style={[pdfStyles.cardValue, pdfStyles.cardValueGreen]}>{fmt(grandTotalPaid)}</Text>
          </View>
          <View style={pdfStyles.cardRed}>
            <Text style={[pdfStyles.cardLabel, pdfStyles.cardLabelRed]}>Total Adeudado</Text>
            <Text style={[pdfStyles.cardValue, pdfStyles.cardValueRed]}>{fmt(grandTotalOwed)}</Text>
          </View>
        </View>
      </Page>
    </Document>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const FranchiseStats = ({ data }) => {
  const barChartRef  = useRef(null);
  const doughnutRef  = useRef(null);

  const labels = data.map((row) => row.name);

  const grandTotalToPay = data.reduce((acc, row) => acc + (row.totalToPay || 0), 0);
  const grandTotalPaid  = data.reduce((acc, row) => acc + (row.totalPaid  || 0), 0);
  const grandTotalOwed  = data.reduce((acc, row) => acc + (row.totalOwed  || 0), 0);

  const doughnutData = {
    labels: ['Total Pagado', 'Total Adeudado'],
    datasets: [
      {
        data: [grandTotalPaid, grandTotalOwed],
        backgroundColor: ['rgba(34, 197, 94, 0.7)', 'rgba(239, 68, 68, 0.7)'],
        borderColor: ['rgba(34, 197, 94, 1)', 'rgba(239, 68, 68, 1)'],
        borderWidth: 1,
      },
    ],
  };

  const doughnutOptions = {
    maintainAspectRatio: false,
    responsive: true,
    plugins: {
      legend: {
        position: 'bottom',
      },
      title: {
        display: true,
        text: 'Distribucion de Pagos vs Adeudos',
      },
      tooltip: {
        callbacks: {
          label: (ctx) => ` ${ctx.label}: $${Number(ctx.raw).toLocaleString('es-MX')}`,
        },
      },
    },
  };

  const chartData = {
    labels,
    datasets: [
      {
        label: 'Total a Pagar',
        data: data.map((row) => row.totalToPay),
        backgroundColor: 'rgba(59, 130, 246, 0.7)',
        borderColor: 'rgba(59, 130, 246, 1)',
        borderWidth: 1,
      },
      {
        label: 'Total Pagado',
        data: data.map((row) => row.totalPaid),
        backgroundColor: 'rgba(34, 197, 94, 0.7)',
        borderColor: 'rgba(34, 197, 94, 1)',
        borderWidth: 1,
      },
      {
        label: 'Total Adeudado',
        data: data.map((row) => row.totalOwed),
        backgroundColor: 'rgba(239, 68, 68, 0.7)',
        borderColor: 'rgba(239, 68, 68, 1)',
        borderWidth: 1,
      },
    ],
  };

  const chartOptions = {
    maintainAspectRatio: false,
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Comparativo Financiero por Franquicia',
      },
    },
    scales: {
      x: {
        stacked: false,
      },
      y: {
        stacked: false,
        ticks: {
          callback: (value) => `$${Number(value).toLocaleString('es-MX')}`,
        },
      },
    },
  };

  const handleDownloadPDF = async () => {
    const barImg      = barChartRef.current.toBase64Image();
    const doughnutImg = doughnutRef.current.toBase64Image();
    const blob = await pdf(
      <FranchiseStatsPDF data={data} barImg={barImg} doughnutImg={doughnutImg} />
    ).toBlob();
    const url  = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href     = url;
    link.download = 'resumen-franquicias.pdf';
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="mt-8">
      <div className="flex justify-end mb-4">
        <Button
          variant="contained"
          startIcon={<PictureAsPdfIcon />}
          onClick={handleDownloadPDF}
        >
          Descargar PDF
        </Button>
      </div>

      <div>
        <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wide mb-4">
          Resumen por Franquicia
        </h3>

        <TableContainer component={Paper} elevation={0} className="border border-gray-200">
          <Table size="small">
            <TableHead>
              <TableRow className="bg-gray-200">
                <TableCell className="text-xs font-bold text-gray-700">FRANQUICIA</TableCell>
                <TableCell align="right" className="text-xs font-bold text-gray-700">ALUMNOS</TableCell>
                <TableCell align="right" className="text-xs font-bold text-gray-700">TOTAL A PAGAR</TableCell>
                <TableCell align="right" className="text-xs font-bold text-gray-700">TOTAL PAGADO</TableCell>
                <TableCell align="right" className="text-xs font-bold text-gray-700">TOTAL ADEUDADO</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((row) => (
                <TableRow key={row.name} hover>
                  <TableCell className="text-xs">{row.name}</TableCell>
                  <TableCell align="right" className="text-xs">{row.students}</TableCell>
                  <TableCell align="right" className="text-xs text-blue-700 font-medium">
                    {formatMoney(row.totalToPay)}
                  </TableCell>
                  <TableCell align="right" className="text-xs text-green-700 font-medium">
                    {formatMoney(row.totalPaid)}
                  </TableCell>
                  <TableCell align="right" className="text-xs text-red-700 font-medium">
                    {formatMoney(row.totalOwed)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <div className="h-[400px] mt-6">
          <Bar ref={barChartRef} options={chartOptions} data={chartData} />
        </div>

        <div className="mt-10">
          <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wide mb-4">
            Resumen General
          </h3>

          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
              <p className="text-xs font-semibold text-blue-600 uppercase tracking-wide mb-1">
                Total General a Pagar
              </p>
              <p className="text-lg font-bold text-blue-800">{formatMoney(grandTotalToPay)}</p>
            </div>
            <div className="rounded-lg border border-green-200 bg-green-50 p-4">
              <p className="text-xs font-semibold text-green-600 uppercase tracking-wide mb-1">
                Total Pagado
              </p>
              <p className="text-lg font-bold text-green-800">{formatMoney(grandTotalPaid)}</p>
            </div>
            <div className="rounded-lg border border-red-200 bg-red-50 p-4">
              <p className="text-xs font-semibold text-red-600 uppercase tracking-wide mb-1">
                Total Adeudado
              </p>
              <p className="text-lg font-bold text-red-800">{formatMoney(grandTotalOwed)}</p>
            </div>
          </div>

          <div className="h-[300px] max-w-sm mx-auto">
            <Doughnut ref={doughnutRef} options={doughnutOptions} data={doughnutData} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default FranchiseStats;
